import type { Project } from '@/types/content'

/**
 * Real work, taken from the CV.
 *
 * Every entry carries the same four picture slots — `cover`, `logo`,
 * `reference` and `gallery` — and every one of them is empty, because no
 * screenshots or mockups have been supplied yet. The components fall back to
 * a branded placeholder rather than a broken image, so the site is finished
 * even while the pictures are not.
 *
 * They are present-and-empty rather than absent on purpose. The admin panel
 * works out its form from the shape of the data, so a field that no project
 * has is a field nothing can upload into — and the pictures would stay
 * missing forever because there would be nowhere to put them.
 *
 * `detail.overview` and `challengeGroups` are written from the CV's one-line
 * descriptions. They are accurate but thin — expand them with the real story
 * of each build (the problem, the constraints, what you decided and why).
 */
export const projects: readonly Project[] = [
  {
    slug: 'e-testing-suite',
    title: 'E-Testing Suite',
    category: 'EdTech Platform',
    cover: '',
    logo: '',
    reference: '',
    gallery: [],
    client: 'ESIGN/UIECC — CDNU',
    year: '2026',
    url: 'https://e-testingsuite.com',
    featured: true,
    stack: ['ReactJS', 'Institutional infrastructure'],
    summary:
      'Institutional online examination platform, built into the university’s digital infrastructure.',
    classifications: [{ domain: 'software', category: 'web-app' }],
    detail: {
      service: 'UI/UX, Frontend',
      overview:
        'An online examination platform for the Centre de Développement Numérique Universitaire. I led the frontend build in ReactJS and integrated it with the university’s existing digital infrastructure.',
      disciplines: ['Frontend', 'EdTech'],
      challengeGroups: [
        {
          title: 'Delivery',
          items: [
            {
              term: 'Examination flows',
              description:
                'Designed and built the sitting, timing and submission paths for institutional assessments.',
            },
            {
              term: 'Infrastructure integration',
              description:
                'Wired the platform into the university systems administered within the CDNU environment.',
            },
          ],
        },
      ],
      conclusion: 'In service as the institution’s online examination platform.',
    },
  },
  {
    slug: 'maguida-ai',
    title: 'Maguida AI',
    category: 'AI Platform',
    cover: '',
    logo: '',
    reference: '',
    gallery: [],
    client: 'Richenel’s AI Agency',
    year: '2024 - 2025',
    url: 'https://www.maguida.cm',
    featured: false,
    stack: ['ReactJS', 'PHP', 'AWS', 'Figma', 'Illustrator'],
    summary:
      'Brand identity redesign and cloud-native AWS deployment for an AI platform.',
    classifications: [
      { domain: 'software', category: 'web-app' },
      { domain: 'design', category: 'logo' },
      { domain: 'infrastructure', category: 'deployment-architecture' },
    ],
    detail: {
      service: 'Brand, UI/UX, Web, Cloud',
      overview:
        'Maguida AI is the agency’s flagship product. I deployed the cloud-native AWS infrastructure and led the brand identity redesign alongside the platform build.',
      disciplines: ['Brand', 'Cloud', 'Web'],
      challengeGroups: [
        {
          title: 'Identity and interface',
          items: [
            {
              term: 'Brand redesign',
              description:
                'Led the identity redesign and applied it across the product and its marketing surfaces.',
            },
            {
              term: 'Platform build',
              description: 'Built the client-facing platform in ReactJS on a PHP backend.',
            },
          ],
        },
        {
          title: 'Infrastructure',
          items: [
            {
              term: 'Cloud-native deployment',
              description:
                'Deployed AWS infrastructure sized to the agency’s needs.',
            },
          ],
        },
      ],
      conclusion:
        'Brand, interface, application and infrastructure delivered from one hand.',
    },
  },
  {
    slug: 'lameute',
    title: 'LAMEUTE',
    category: 'Studio Site',
    cover: '',
    logo: '',
    reference: '',
    gallery: [],
    client: 'LAMEUTE',
    year: '2022 - present',
    url: 'https://lameute.cm',
    featured: false,
    stack: [
      'ReactJS', 'Tailwind CSS', 'ExpressJS', 'PostgreSQL',
      'AWS', 'Docker', 'Kubernetes', 'GitHub Actions',
    ],
    summary:
      "The studio's own site and the design system the rest of its products are built from.",
    classifications: [
      { domain: 'software', category: 'web-app' },
      { domain: 'design', category: 'design-system' },
      { domain: 'infrastructure', category: 'deployment-architecture' },
    ],
    detail: {
      service: 'Brand, UI/UX, Full-stack, Cloud',
      overview:
        "LAMEUTE's own website, and the base the studio's other products build on. I handled brand design, UI/UX, the full-stack build and the cloud infrastructure behind it.",
      disciplines: ['Brand', 'Full-stack', 'Cloud'],
      challengeGroups: [
        {
          title: 'Design and build',
          items: [
            {
              term: 'Identity',
              description:
                'Brand design and a reusable visual language applied across the studio’s products.',
            },
            {
              term: 'Full-stack delivery',
              description:
                'ReactJS and Tailwind on the front, ExpressJS and PostgreSQL with Prisma behind it.',
            },
          ],
        },
        {
          title: 'Infrastructure',
          items: [
            {
              term: 'Cloud setup',
              description:
                'Designed and provisioned the AWS infrastructure, containerised with Docker.',
            },
            {
              term: 'Continuous delivery',
              description:
                'GitHub Actions pipelines so deploys are repeatable rather than manual.',
            },
          ],
        },
      ],
      conclusion:
        'A studio presence that doubles as the reference implementation for every project that followed it.',
    },
  },
  {
    slug: 'repavi-lodges',
    title: 'RepAvi Lodges',
    category: 'Booking Platform',
    cover: '',
    logo: '',
    reference: '',
    gallery: [],
    client: 'RepAvi',
    year: '2024',
    url: 'https://repavilodges.com',
    featured: false,
    stack: ['ReactJS', 'Tailwind CSS', 'ExpressJS', 'PostgreSQL'],
    summary: 'Lodge booking platform with accounts and reservation management.',
    classifications: [{ domain: 'software', category: 'web-app' }],
    detail: {
      service: 'UI/UX, Full-stack',
      overview:
        'A booking platform for RepAvi Lodges, covering guest accounts, availability and reservations.',
      disciplines: ['UI/UX', 'Full-stack'],
      challengeGroups: [
        {
          title: 'Product',
          items: [
            {
              term: 'Booking flow',
              description:
                'Designed and built the reservation path from search through confirmation.',
            },
            {
              term: 'Accounts',
              description: 'Authentication and guest account management.',
            },
          ],
        },
      ],
      conclusion:
        'A working booking platform that replaced a manual reservation process.',
    },
  },
  {
    slug: 'autofish-store',
    title: 'Autofish Store',
    category: 'Mobile App',
    cover: '',
    logo: '',
    reference: '',
    gallery: [],
    client: 'Autofish',
    year: '2025',
    featured: false,
    stack: ['ReactJS', 'Vite', 'Tailwind CSS', 'Capacitor', 'Figma'],
    summary:
      "Brand, interface and Android build of a marketplace app, shipped with Capacitor.",
    classifications: [
      { domain: 'software', category: 'mobile-app' },
      { domain: 'design', category: 'logo' },
    ],
    detail: {
      service: 'Brand, UI/UX, Mobile development',
      overview:
        "Autofish's marketplace application for Android. I joined the team to lead brand design and UI/UX, then built the app itself.",
      disciplines: ['Brand', 'UI/UX', 'Mobile'],
      challengeGroups: [
        {
          title: 'Design',
          items: [
            {
              term: 'Brand design',
              description: 'Established the marketplace’s visual identity.',
            },
            {
              term: 'Interface design',
              description:
                'Designed the marketplace flows in Figma for a mobile-first audience.',
            },
          ],
        },
        {
          title: 'Delivery',
          items: [
            {
              term: 'Cross-platform build',
              description:
                'Built in React with Vite and packaged for Android with Capacitor, so one codebase serves web and mobile.',
            },
          ],
        },
      ],
      conclusion:
        'A marketplace app taken from brand to a shipped Android build inside six months.',
    },
  },
  {
    slug: 'checkme',
    title: 'CheckMe SaaS',
    category: 'Cloud Platform',
    cover: '',
    logo: '',
    reference: '',
    gallery: [],
    client: 'LAMEUTE / ESIGN-UIECC',
    year: '2023 - 2024',
    url: 'https://checkme.lameute.cm',
    featured: true,
    stack: ['ReactJS', 'AWS EC2', 'VMware ESXi', 'Docker', 'PostgreSQL', 'JWT'],
    summary:
      'Attendance SaaS with a dual cloud/on-premise architecture and biometric access control — also the subject of my MEng thesis.',
    classifications: [
      { domain: 'infrastructure', category: 'deployment-architecture' },
      { domain: 'infrastructure', category: 'network-architecture' },
      { domain: 'software', category: 'web-app' },
    ],
    detail: {
      service: 'UI/UX, Full-stack, Cloud, Security',
      overview:
        'CheckMe is a SaaS platform deployed two ways from one codebase: on AWS EC2, and on-premise inside a VMware ESXi university data centre. It was the subject of my MEng thesis, graded Excellent.',
      disciplines: ['Cloud', 'Security', 'Full-stack'],
      challengeGroups: [
        {
          title: 'Deployment architecture',
          items: [
            {
              term: 'Dual targets',
              description:
                'One containerised build serving both AWS EC2 and an on-premise VMware ESXi cluster, so the institution could choose where its data lived.',
            },
            {
              term: 'Pipelines and transport security',
              description:
                'Full containerisation via Docker, CI/CD pipelines, and end-to-end TLS.',
            },
          ],
        },
        {
          title: 'Access control',
          items: [
            {
              term: 'Multi-modal biometrics',
              description:
                'Fingerprint and QR-code authentication with JWT session management.',
            },
            {
              term: 'Role hierarchy at scale',
              description:
                'A six-level role-based access control hierarchy supporting 10,000+ concurrent users.',
            },
          ],
        },
      ],
      conclusion:
        'Delivered as a working platform and defended as an MEng thesis, graded Excellent.',
    },
  },
  {
    slug: 'libroware',
    title: 'Libroware LMS',
    category: 'Learning Platform',
    cover: '',
    logo: '',
    reference: '',
    gallery: [],
    client: 'ESIGN/UIECC',
    year: '2023',
    url: 'https://libroware.lameute.cm',
    featured: false,
    stack: ['ReactJS', 'Tailwind CSS', 'ExpressJS', 'PostgreSQL'],
    summary:
      'Learning management system designed and delivered for ESIGN/UIECC (esign.cm).',
    classifications: [
      { domain: 'software', category: 'web-app' },
      { domain: 'infrastructure', category: 'deployment-architecture' },
    ],
    detail: {
      service: 'UI/UX, Full-stack, Cloud',
      overview:
        'A learning management system built at LAMEUTE and delivered to ESIGN/UIECC, running on the studio’s shared platform and AWS infrastructure.',
      disciplines: ['UI/UX', 'Full-stack'],
      challengeGroups: [
        {
          title: 'Build',
          items: [
            {
              term: 'Full-stack delivery',
              description:
                'Designed and built the application on the studio’s React and Express foundation.',
            },
          ],
        },
      ],
      conclusion: 'Delivered on the studio’s shared platform.',
    },
  },
  {
    slug: 'ifossa',
    title: 'IFOSSA',
    category: 'Web Platform',
    cover: '',
    logo: '',
    reference: '',
    gallery: [],
    client: 'LAMEUTE',
    year: '2023',
    url: 'https://ifossah.com',
    featured: false,
    stack: ['ReactJS', 'Tailwind CSS', 'ExpressJS', 'PostgreSQL'],
    summary: 'Client web platform delivered through LAMEUTE.',
    classifications: [{ domain: 'software', category: 'web-app' }],
    detail: {
      service: 'UI/UX, Full-stack',
      overview:
        'A client platform delivered through LAMEUTE, covering design and the full-stack build.',
      disciplines: ['UI/UX', 'Full-stack'],
      challengeGroups: [
        {
          title: 'Build',
          items: [
            {
              term: 'Full-stack delivery',
              description:
                'Designed and built the platform end to end on the studio’s stack.',
            },
          ],
        },
      ],
      conclusion: 'Delivered for the client on the studio’s platform.',
    },
  },
  {
    slug: 'driv',
    title: 'Driv Covoiturage',
    category: 'Mobile App',
    cover: '',
    logo: '',
    reference: '',
    gallery: [],
    client: 'LAMEUTE',
    year: '2024',
    featured: false,
    stack: ['ReactJS', 'Capacitor', 'ExpressJS', 'PostgreSQL'],
    summary:
      'Carpooling application, published to the Play Store. Second Prize at the cgweb Startup National Coding Contest 2024.',
    classifications: [{ domain: 'software', category: 'mobile-app' }],
    detail: {
      service: 'UI/UX, Mobile development',
      overview:
        'Driv is a carpooling application built at LAMEUTE and published to the Google Play Store.',
      disciplines: ['UI/UX', 'Mobile'],
      challengeGroups: [
        {
          title: 'Build',
          items: [
            {
              term: 'Ride matching',
              description:
                'Designed and built the flows connecting drivers and passengers.',
            },
            {
              term: 'Store release',
              description: 'Packaged and published the Android build to the Play Store.',
            },
          ],
        },
      ],
      conclusion: 'Shipped to the Play Store as a published Android application.',
    },
  },
]
